import { useMutation, useQueryClient } from '@tanstack/react-query'
import { type components } from 'api/playerPayback/api'
import { useTypedApiClient } from 'hooks/useTypedApiClient'
import { type ApiError } from 'openapi-typescript-fetch'

interface usePutRewardProgramProps {
  onSuccess?: (
    data: components['schemas']['FullRewardsProgramDTO'],
    variables: {
      id: number
      rewardsProgramData: components['schemas']['RewardsProgramDTO']
    }
  ) => void
  onError?: (
    error: ApiError,
    variables: {
      id: number
      rewardsProgramData: components['schemas']['RewardsProgramDTO']
    }
  ) => void
}

export const usePutRewardsProgram = ({
  onSuccess,
  onError,
}: usePutRewardProgramProps) => {
  const put = useTypedApiClient({
    path: '/admin/rewards-programs/{id}',
    method: 'put',
  })
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      id,
      rewardsProgramData,
    }: {
      id: number
      rewardsProgramData: components['schemas']['RewardsProgramDTO']
    }) => {
      const { data } = await put({ ...rewardsProgramData, id })
      return data
    },
    onSuccess: async (
      data: components['schemas']['FullRewardsProgramDTO'],
      variables: {
        id: number
        rewardsProgramData: components['schemas']['RewardsProgramDTO']
      }
    ) => {
      await queryClient.invalidateQueries({
        queryKey: [
          '/admin/rewards-programs/corporate-accounts/{id}',
          data.corporateAccountId,
        ],
      })
      await queryClient.invalidateQueries({
        queryKey: [
          '/admin/rewards-programs/licensed-establishments/{id}',
          data.licensedEstablishmentId,
        ],
      })
      await queryClient.invalidateQueries({
        queryKey: [
          '/admin/rewards-programs/organization/{id}',
          data.organizationId,
        ],
      })
      onSuccess?.(data, variables)
    },
    onError,
  })
}
